import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { QRCodeSVG } from 'qrcode.react';

const UniversalQRGenerator = ({ allUsers, onQRGenerated }) => {
  const { currentTenant } = useAuth();
  const [selectedUser, setSelectedUser] = useState('');
  const [qrType, setQrType] = useState('access');
  const [expiryHours, setExpiryHours] = useState('never');
  const [generatedQR, setGeneratedQR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const qrTypes = [
    { value: 'access', label: 'Facility Access', description: 'For gym entry and exit' },
    { value: 'attendance', label: 'Attendance Tracking', description: 'For staff check-in/out' },
    { value: 'profile', label: 'Profile Sharing', description: 'Share member profile' }
  ];

  const expiryOptions = [
    { value: '1', label: '1 Hour' },
    { value: '24', label: '24 Hours' },
    { value: '168', label: '1 Week' },
    { value: '720', label: '1 Month' },
    { value: 'never', label: 'Never Expires' }
  ];

  const handleGenerateQR = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setGeneratedQR(null);

    try {
      const user = allUsers?.find(u => u?.user_id === selectedUser);
      const qrData = `${qrType?.toUpperCase()}-${currentTenant?.id?.substring(0, 8)}-${selectedUser?.substring(0, 8)}-${Date.now()}`;
      
      let expiresAt = null;
      if (expiryHours !== 'never') {
        const expiryDate = new Date();
        expiryDate?.setHours(expiryDate?.getHours() + parseInt(expiryHours));
        expiresAt = expiryDate?.toISOString();
      }

      // Determine which table to use based on user role
      const isStaff = ['gym_owner', 'staff', 'trainer']?.includes(user?.role);
      
      if (isStaff && qrType === 'attendance') {
        // Use staff_qr_codes table
        const { data, error } = await supabase?.from('staff_qr_codes')?.insert({
            tenant_id: currentTenant?.id,
            user_id: selectedUser,
            qr_code_data: qrData,
            is_active: true,
            expires_at: expiresAt,
            generated_at: new Date()?.toISOString()
          })?.select()?.single();

        if (error) throw error;
        setGeneratedQR({ ...data, source: 'staff', user });
      } else {
        // Use member_barcodes table
        const { data, error } = await supabase?.from('member_barcodes')?.insert({
            tenant_id: currentTenant?.id,
            user_id: selectedUser,
            barcode_data: qrData,
            barcode_type: 'qr_code',
            is_active: true,
            expires_at: expiresAt,
            generated_at: new Date()?.toISOString()
          })?.select()?.single();

        if (error) throw error;
        setGeneratedQR({ ...data, source: 'member', user });
      }

      setSuccess('QR code generated successfully!');
      onQRGenerated?.();
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('universal-qr-code');
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas?.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-code-${generatedQR?.user?.user_profiles?.full_name?.replace(/\s+/g, '-')}.png`;
      downloadLink.href = pngFile;
      downloadLink?.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const selectedUserData = allUsers?.find(u => u?.user_id === selectedUser);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Generation Form */}
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <div className="flex items-center gap-2 mb-6">
          <Icon name="Plus" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">Generate QR Code</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select User
            </label>
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e?.target?.value)}
            >
              <option value="">Choose a user...</option>
              <optgroup label="Gym Owners">
                {allUsers?.filter(u => u?.role === 'gym_owner')?.map((user) => (
                  <option key={user?.user_id} value={user?.user_id}>
                    {user?.user_profiles?.full_name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Staff">
                {allUsers?.filter(u => u?.role === 'staff')?.map((user) => (
                  <option key={user?.user_id} value={user?.user_id}>
                    {user?.user_profiles?.full_name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Trainers">
                {allUsers?.filter(u => u?.role === 'trainer')?.map((user) => (
                  <option key={user?.user_id} value={user?.user_id}>
                    {user?.user_profiles?.full_name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Members">
                {allUsers?.filter(u => u?.role === 'member')?.map((user) => (
                  <option key={user?.user_id} value={user?.user_id}>
                    {user?.user_profiles?.full_name}
                  </option>
                ))}
              </optgroup>
            </Select>
          </div>

          {selectedUserData && (
            <div className="p-3 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{selectedUserData?.user_profiles?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedUserData?.role} • {selectedUserData?.user_profiles?.email}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              QR Code Type
            </label>
            <Select
              value={qrType}
              onChange={(e) => setQrType(e?.target?.value)}
            >
              {qrTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label} - {type?.description}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Expiration
            </label>
            <Select
              value={expiryHours}
              onChange={(e) => setExpiryHours(e?.target?.value)}
            >
              {expiryOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-sm text-green-500">{success}</p>
            </div>
          )}

          <Button
            onClick={handleGenerateQR}
            disabled={loading || !selectedUser}
            className="w-full"
          >
            {loading ? 'Generating...' : 'Generate QR Code'}
          </Button>
        </div>
      </div>
      {/* QR Code Display */}
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        {generatedQR ? (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-foreground">Generated QR Code</h3>

            <div className="flex justify-center p-6 bg-white rounded-lg">
              <QRCodeSVG
                id="universal-qr-code"
                value={generatedQR?.qr_code_data || generatedQR?.barcode_data}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleDownloadQR}
                className="w-full flex items-center justify-center gap-2"
              >
                <Icon name="Download" size={18} />
                Download QR Code
              </Button>
            </div>

            <div className="p-4 bg-background rounded-lg border border-border space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">User:</span>
                <span className="font-medium text-foreground">{generatedQR?.user?.user_profiles?.full_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Type:</span>
                <span className="font-medium text-foreground">{qrType}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Expires:</span>
                <span className="font-medium text-foreground">
                  {generatedQR?.expires_at ? new Date(generatedQR?.expires_at)?.toLocaleDateString() : 'Never'}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-12">
            <Icon name="QrCode" size={64} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No QR Code Generated</p>
            <p className="text-sm mt-2">Select a user and generate their QR code</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalQRGenerator;